import React from 'react';
import { Drawer, Row, Col, Typography, Form, Button } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_STANDARDIZED_ADDRESS_DETAIL, GetStandardizedAddressDetailResponse } from '../graphql/queries/getStandardizedAddressDetail';

const { Text } = Typography;

/** 门禁码类型展示名（与原编辑态下拉选项保持一致） */
const CODE_TYPE_LABELS: Record<string, string> = {
  Gate: '社区门禁',
  MainDoor: '楼门门禁',
  SideDoor: '侧门门禁',
  Elevate: '电梯门禁',
  Garage: '车库门禁',
  ParcelStop: '快递柜',
};

/** 门禁码状态展示名：0-待验证，1-可用，2-不可用 */
const CODE_STATUS_LABELS: Record<number, string> = {
  0: '待验证',
  1: '可用',
  2: '不可用',
};

interface StandardizedAddressDetailDrawerProps {
  visible: boolean;
  onClose: () => void;
  addressId: string | null;
}

/**
 * 标准化地址详情抽屉（只读）
 *
 * 坐标、黑名单、Code 信息均不在此处维护：坐标走列表页「更新经纬度」，
 * 黑名单与 Code 维护入口已收回。
 */
const StandardizedAddressDetailDrawer: React.FC<StandardizedAddressDetailDrawerProps> = ({
  visible,
  onClose,
  addressId,
}) => {
  const { data, loading } = useQuery<GetStandardizedAddressDetailResponse>(
    GET_STANDARDIZED_ADDRESS_DETAIL,
    {
      variables: {
        id: addressId,
      },
      skip: !addressId || !visible,
      fetchPolicy: 'network-only',
    }
  );

  const detail = data?.getStandardizedAddressDetail;
  const hasCoordinates = !!detail?.latitude && !!detail?.longitude;

  return (
    <Drawer
      title="标准化地址详情"
      placement="right"
      width={800}
      open={visible}
      onClose={onClose}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
    >
      {loading ? (
        <div className="text-center py-8">加载中...</div>
      ) : !detail ? (
        <div className="text-center py-8">未找到地址信息</div>
      ) : (
        <Form layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <Form.Item label="ID">
            <Text>{detail.id}</Text>
          </Form.Item>

          <Form.Item label="完整地址">
            <Text>{detail.fullStandardAddress}</Text>
          </Form.Item>

          <Form.Item label="更新时间">
            <Text>{detail.updatedAt ? new Date(detail.updatedAt).toLocaleString() : '-'}</Text>
          </Form.Item>

          <Form.Item label="坐标">
            <Text>
              {hasCoordinates ? `${detail.latitude}, ${detail.longitude}` : '-'}
            </Text>
          </Form.Item>

          <Form.Item label="邮编">
            <Text>{detail.zipcode || '-'}</Text>
          </Form.Item>

          <Form.Item label="主门牌号">
            <Text>{detail.primaryNumber || '-'}</Text>
          </Form.Item>

          <Form.Item label="街道名称">
            <Text>{detail.streetName || '-'}</Text>
          </Form.Item>

          <Form.Item label="单元类型">
            <Text>{detail.unitType || '-'}</Text>
          </Form.Item>

          <Form.Item label="单元号">
            <Text>{detail.unitNumber || '-'}</Text>
          </Form.Item>

          <Form.Item label="城市名称">
            <Text>{detail.cityName || '-'}</Text>
          </Form.Item>

          <Form.Item label="州缩写">
            <Text>{detail.stateAbbreviation || '-'}</Text>
          </Form.Item>

          <Form.Item label="Code 信息">
            {detail.accessCodes.length === 0 ? (
              <Text>-</Text>
            ) : (
              <div>
                {detail.accessCodes.map((code) => (
                  <Row key={code.id} gutter={8} style={{ marginBottom: 4 }}>
                    <Col span={8}>
                      <Text>{CODE_TYPE_LABELS[code.codeType] || code.codeType || '-'}</Text>
                    </Col>
                    <Col span={8}>
                      <Text>{code.accessCode || '-'}</Text>
                    </Col>
                    <Col span={8}>
                      <Text>{CODE_STATUS_LABELS[code.status] ?? code.status}</Text>
                    </Col>
                  </Row>
                ))}
              </div>
            )}
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
};

export default StandardizedAddressDetailDrawer;
